import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { CurrentUserService } from './current-user-service';
import { NotificationService } from './notification.service';

export interface ApiResponse<T> {
  data: T;
  message?: string;
  success: boolean;
}

export interface ApiError {
  message: string;
  code?: string;
  errors?: Record<string, string[]>;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private http = inject(HttpClient);
  private baseUrl: string = environment.apiUrl;
  private router = inject(Router);
  private currentUserService = inject(CurrentUserService);
  private notification = inject(NotificationService);

  /**
   * Get base URL
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }

  /**
   * Build full URL from endpoint
   */
  private buildUrl(endpoint: string): string {
    // Remove leading slash from endpoint if present
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    // Ensure baseUrl doesn't end with slash
    const cleanBaseUrl = this.baseUrl.endsWith('/') ? this.baseUrl.slice(0, -1) : this.baseUrl;
    return `${cleanBaseUrl}/${cleanEndpoint}`;
  }

  /**
   * Create default headers
   */
  private getHeaders(customHeaders?: HttpHeaders): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    // Add authorization token if available
    const token = this.getAuthToken();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    // Merge with custom headers if provided
    if (customHeaders) {
      customHeaders.keys().forEach(key => {
        const values = customHeaders.getAll(key);
        if (values) {
          values.forEach(value => {
            headers = headers.append(key, value);
          });
        }
      });
    }

    return headers;
  }

  /**
   * Get authentication token from storage
   */
  private getAuthToken(): string | null {
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('currentUser') || sessionStorage.getItem('currentUser')
      return token || null;
    }
    return null;
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'Wystąpił nieoczekiwany błąd';
    let errorCode: string | undefined;

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = error.error.message || errorMessage;
    } else {
      // Server-side error
      errorCode = error.error?.code || error.status?.toString();

      if (error.error?.error) {
        errorMessage = error.error.error;
      } else {

        // Handle specific status codes
        switch (error.status) {
          case 400:
            errorMessage = error.error?.message || 'Nieprawidłowe żądanie';
            break;
          case 401:
            errorMessage = 'Brak autoryzacji. Zaloguj się ponownie.';

            this.currentUserService.logout(true)
            break;
          case 403:
            errorMessage = 'Brak uprawnień do wykonania tej operacji';
            break;
          case 404:
            errorMessage = 'Nie znaleziono zasobu';
            break;
          case 422:
            errorMessage = error.error?.message || 'Błąd walidacji danych';
            break;
          case 500:
            errorMessage = 'Błąd serwera. Spróbuj ponownie później.';
            break;
          default:
            if (error.status) {
              errorMessage = error.error?.message || `Błąd ${error.status}: ${error.statusText}`;
            }
        }
      }
    }

    const apiError: ApiError = {
      message: errorMessage,
      code: errorCode,
      errors: error.error?.errors
    };

    this.notification.error(apiError.message, 'Zamknij');
    console.error('API Error:', apiError, error);
    return throwError(() => apiError);
  }

  /**
   * GET request
   */
  get<T>(endpoint: string, params?: HttpParams | Record<string, string | number | boolean>, customHeaders?: HttpHeaders): Observable<T> {
    let httpParams = new HttpParams();
    if (params) {
      if (params instanceof HttpParams) {
        httpParams = params;
      } else {
        Object.keys(params).forEach(key => {
          if (params[key] !== null && params[key] !== undefined) {
            httpParams = httpParams.set(key, params[key].toString());
          }
        });
      }
    }

    return this.http.get<T>(this.buildUrl(endpoint), {
      headers: this.getHeaders(customHeaders),
      params: httpParams
    }).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  /**
   * POST request
   */
  post<T>(endpoint: string, body: unknown, customHeaders?: HttpHeaders): Observable<T> {
    return this.http.post<T>(this.buildUrl(endpoint), body, {
      headers: this.getHeaders(customHeaders)
    }).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  /**
   * PUT request
   */
  put<T>(endpoint: string, body: unknown, customHeaders?: HttpHeaders): Observable<T> {
    return this.http.put<T>(this.buildUrl(endpoint), body, {
      headers: this.getHeaders(customHeaders)
    }).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  /**
   * PATCH request
   */
  patch<T>(endpoint: string, body: unknown, customHeaders?: HttpHeaders): Observable<T> {
    return this.http.patch<T>(this.buildUrl(endpoint), body, {
      headers: this.getHeaders(customHeaders)
    }).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  /**
   * DELETE request
   */
  delete<T>(endpoint: string, customHeaders?: HttpHeaders): Observable<T> {
    return this.http.delete<T>(this.buildUrl(endpoint), {
      headers: this.getHeaders(customHeaders)
    }).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  //   /**
  //    * Upload file
  //    */
  //   uploadFile<T>(endpoint: string, file: File, additionalData?: Record<string, string | number | boolean>): Observable<T> {
  //     const formData = new FormData();
  //     formData.append('file', file);

  //     if (additionalData) {
  //       Object.keys(additionalData).forEach(key => {
  //         const value = additionalData[key];
  //         formData.append(key, value.toString());
  //       });
  //     }

  //     let headers = new HttpHeaders();
  //     const token = this.getAuthToken();
  //     if (token) {
  //       headers = headers.set('Authorization', `Bearer ${token}`);
  //     }

  //     return this.http.post<T>(this.buildUrl(endpoint), formData, {
  //       headers: headers
  //     }).pipe(
  //       catchError(this.handleError.bind(this))
  //     );
  //   }

  //   /**
  //    * Download file
  //    */
  //   downloadFile(endpoint: string, params?: HttpParams | Record<string, string | number | boolean>): Observable<Blob> {
  //     let httpParams = new HttpParams();

  //     if (params) {
  //       if (params instanceof HttpParams) {
  //         httpParams = params;
  //       } else {
  //         Object.keys(params).forEach(key => {
  //           if (params[key] !== null && params[key] !== undefined) {
  //             httpParams = httpParams.set(key, params[key].toString());
  //           }
  //         });
  //       }
  //     }

  //     return this.http.get(this.buildUrl(endpoint), {
  //       headers: this.getHeaders(),
  //       params: httpParams,
  //       responseType: 'blob'
  //     }).pipe(
  //       catchError(this.handleError.bind(this))
  //     );
  //   }
}
